import { FormData } from "./utils";

/**
 * Values the ERP hands us when a staffer starts a consultant contract from a
 * work request (ERP-741). The ERP resolves its own ids to CRM ids before
 * sending, so everything here is already in our terms.
 *
 * Nothing is required beyond the consultant: the ERP sends what that work
 * request happens to hold, and a field it has no value for is simply absent.
 * This is a head start on typing, never a claim that the contract is complete
 * — every value stays editable and the form's own validation is unchanged.
 */
export interface ErpContractPrefill {
  hospitalRef?: string;
  hospitalName?: string;
  departmentsRefs?: string;
  profession?: string;
  areaOfExpertise?: string;
  specializations?: string;
  startDate?: string;
  endDate?: string;
  isNationalProcurement?: boolean;
}

/** Trims to undefined so a blank query param never overwrites a real default. */
function clean(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * `YYYY-MM-DD` → a local-midnight `Date`, which is what the form's date fields
 * hold. Built from the parts rather than `new Date(string)`: the string form
 * is parsed as UTC, which lands on the previous day for anyone behind it.
 * An unparseable value yields undefined so the field keeps its own default.
 */
function toDate(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return undefined;
  }
  return new Date(year, month - 1, day);
}

/**
 * Read a prefill off a standalone URL — the path the ERP takes when it is not
 * embedded in this app and the browser simply follows its link
 * (`/consultants/:id/contract/new?hospitalRef=…`).
 */
export function erpPrefillFromSearch(search: string): ErpContractPrefill | undefined {
  const params = new URLSearchParams(search);
  const prefill: ErpContractPrefill = {
    hospitalRef: clean(params.get("hospitalRef")),
    hospitalName: clean(params.get("hospitalName")),
    departmentsRefs: clean(params.get("departmentsRefs")),
    profession: clean(params.get("profession")),
    areaOfExpertise: clean(params.get("areaOfExpertise")),
    specializations: clean(params.get("specializations")),
    startDate: clean(params.get("startDate")),
    endDate: clean(params.get("endDate")),
    isNationalProcurement: params.get("isNationalProcurement") === "true"
  };
  return Object.values(prefill).some(Boolean) ? prefill : undefined;
}

/**
 * Fold a prefill onto the form's own defaults.
 *
 * Only keys the ERP actually sent are written, so an absent one keeps the
 * default the form would have started with. `HospitalRef` needs a label as
 * well as an id because the customer picker renders the label directly; when
 * the ERP sends no name we fall back to the id so the field is at least
 * traceable rather than blank.
 */
export function applyErpPrefill(
  initialValues: FormData,
  prefill: ErpContractPrefill | undefined
): FormData {
  if (!prefill) {
    return initialValues;
  }

  const next: FormData = { ...initialValues };

  if (prefill.hospitalRef) {
    next.HospitalRef = {
      value: prefill.hospitalRef,
      label: prefill.hospitalName || prefill.hospitalRef
    };
  }
  if (prefill.departmentsRefs) {
    next.DepartmentsRefs = [prefill.departmentsRefs];
  }
  if (prefill.profession) {
    next.Profession = prefill.profession;
  }
  if (prefill.areaOfExpertise) {
    next.AreaOfExpertise = prefill.areaOfExpertise;
  }
  if (prefill.specializations) {
    next.Specializations = [prefill.specializations];
  }
  const startDate = toDate(prefill.startDate);
  if (startDate) {
    next.StartDate = startDate;
  }
  const endDate = toDate(prefill.endDate);
  if (endDate) {
    next.EndDate = endDate;
  }
  // Only ever turned on from here. The ERP omits the flag for an ordinary
  // need, and leaving the form's own null in place keeps the "national
  // procurement?" radio unanswered rather than silently answering it "no".
  if (prefill.isNationalProcurement) {
    next.isNationalProcurementPriceStrategy = true;
  }

  return next;
}
